import { useState, useRef, useEffect, type CSSProperties } from "react";
import { useInviteMember } from "../../hooks/use-workspace-members";

interface InviteMemberModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (email: string) => void;
}

export function InviteMemberModal({ open, onClose, onSuccess }: InviteMemberModalProps) {
  const [email, setEmail] = useState("");
  const emailRef = useRef<HTMLInputElement>(null);
  const invite = useInviteMember();
  const prevOpen = useRef(false);

  if (open && !prevOpen.current) {
    setEmail("");
  }
  prevOpen.current = open;

  useEffect(() => {
    if (open) setTimeout(() => emailRef.current?.focus(), 50);
  }, [open]);

  if (!open) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    invite.mutate(
      { email: email.trim(), role: "member" },
      {
        onSuccess: () => {
          onSuccess?.(email.trim());
          onClose();
        },
      },
    );
  }

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    fontFamily: "var(--font-sans)",
  };

  const modal: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-lg)",
    padding: 24,
    width: 400,
    maxWidth: "90vw",
  };

  const field: CSSProperties = { marginBottom: 16 };

  const label: CSSProperties = {
    display: "block",
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    marginBottom: 4,
  };

  const input: CSSProperties = {
    width: "100%",
    fontSize: 13,
    padding: "8px 10px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
  };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modal} role="dialog" aria-label="Invite member">
        <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 16 }}>
          Invite member
        </div>

        <form onSubmit={handleSubmit}>
          <div style={field}>
            <label style={label}>Email</label>
            <input
              ref={emailRef}
              type="email"
              style={input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="katya@example.com"
            />
          </div>

          <div style={field}>
            <p style={{ fontSize: 12, color: "var(--color-text-tertiary)", lineHeight: 1.5, margin: 0 }}>
              Will be invited as a <strong style={{ color: "var(--color-text-secondary)", fontWeight: 500 }}>Member</strong>.
              Members can view and edit content but cannot manage workspace settings, schema, or other members.
            </p>
          </div>

          {invite.isError && (
            <div style={{ fontSize: 12, color: "var(--c-red-600)", marginBottom: 12 }}>
              {invite.error?.message ?? "Failed to send invitation"}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ fontSize: 13, padding: "8px 14px" }}>
              Cancel
            </button>
            <button
              type="submit"
              data-variant="primary"
              disabled={!email.trim() || invite.isPending}
              style={{ fontSize: 13, padding: "8px 14px" }}
            >
              {invite.isPending ? "Sending..." : "Send invitation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
